import { useState, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api, { backendUrl } from "@/lib/api";
import { Send, Loader2, FileText, MessageCircle, AlertTriangle, Cloud, Mail, CalendarClock, ClipboardEdit } from "lucide-react";
import DocumentoDialog from "@/components/DocumentoDialog";
import VeraBriefing from "@/components/VeraBriefing";
import { ASSISTANT_NAME, ASSISTANT_GREETING } from "@/lib/assistant";
import { useAuth } from "@/context/AuthContext";

const SUGGESTIONS = [
  { t: "Quali pratiche sono ancora aperte?" },
  { t: "Chi mi deve ancora dei soldi e quanto?", soldi: true },
  { t: "Ci sono documenti o permessi in scadenza nei prossimi 60 giorni?" },
  { t: "Quali pratiche sono ferme da più di un mese?" },
  { t: "Riepilogo incassi e da incassare per tipo di pratica", soldi: true },
];

const SESSION_KEY = "rb_chat_session";
const sessionId = (() => {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) { s = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem(SESSION_KEY, s); }
  return s;
})();

function docUrl(id) {
  const token = localStorage.getItem("rb_token");
  return `${backendUrl}/api/documenti/${id}/download?token=${token}`;
}

let msgSeq = 0;
const nextMsgId = () => ++msgSeq;

export default function ChatPanel() {
  const { can } = useAuth();
  const vedeSoldi = can("vedi_dati_economici");
  const qc = useQueryClient();
  const [messages, setMessages] = useState([
    { id: nextMsgId(), role: "assistant", text: ASSISTANT_GREETING },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);
  const [pendingText, setPendingText] = useState("");
  const [docPreset, setDocPreset] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const push = (m) => setMessages((prev) => [...prev, { id: nextMsgId(), ...m }]);
  const refresh = () => qc.invalidateQueries();

  const send = async (text) => {
    const msg = text ?? input;
    if (!msg.trim() || loading) return;
    push({ role: "user", text: msg });
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { message: msg, session_id: sessionId });
      if (data.requires_confirmation) {
        setPending(data.pending_action);
        setPendingText(data.reply || "");
        push({ role: "assistant", text: data.reply, confirm: true });
      } else {
        push({ role: "assistant", text: data.reply, action: data.action_type, data: data.data });
        refresh();
      }
    } catch {
      push({ role: "assistant", text: "Si è verificato un errore. Riprova." });
    } finally {
      setLoading(false);
    }
  };

  const confirm = async (ok) => {
    const action = pending;
    setPending(null);
    if (!ok) { push({ role: "assistant", text: "Operazione annullata." }); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/chat/execute", action);
      push({ role: "assistant", text: data.reply });
      refresh();
    } catch {
      push({ role: "assistant", text: "Errore durante l'esecuzione." });
    } finally { setLoading(false); }
  };

  const renderExtra = (m) => {
    const d = m.data || {};
    if (m.action === "documento_incompleto") {
      return (
        <div className="mt-3">
          <button data-testid="chat-complete-doc" onClick={() => setDocPreset(d)}
            className="inline-flex items-center gap-2 text-xs bg-[#1A2B4A] text-white px-3 py-2 rounded-sm hover:bg-[#22345c] transition-colors">
            <ClipboardEdit className="w-4 h-4" /> Completa e genera
          </button>
        </div>
      );
    }
    const doc = d.documento || d.fattura?.documento;
    const wa = d.wa_link || d.fattura?.wa_link;
    const drive = d.drive_link || d.fattura?.drive_link;
    return (
      <div className="mt-3 space-y-2">
        {doc && (
          <a data-testid="chat-doc-download" href={docUrl(doc.id)} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 text-xs bg-[#f9f8f5] border border-[#1A2B4A]/10 px-3 py-2 rounded-sm hover:border-[#C9A84C] transition-colors">
            <FileText className="w-4 h-4 text-[#1A2B4A]" /> Scarica {doc.filename}
          </a>
        )}
        {drive && (
          <a data-testid="chat-drive-link" href={drive} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 text-xs bg-[#f9f8f5] border border-[#1A2B4A]/10 px-3 py-2 rounded-sm hover:border-[#C9A84C] transition-colors">
            <Cloud className="w-4 h-4 text-[#1A2B4A]" /> Apri su Google Drive
          </a>
        )}
        {d.draft_link && (
          <a href={d.draft_link} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 text-xs bg-[#f9f8f5] border border-[#1A2B4A]/10 px-3 py-2 rounded-sm hover:border-[#C9A84C] transition-colors">
            <Mail className="w-4 h-4 text-[#1A2B4A]" /> Rivedi bozza in Gmail
          </a>
        )}
        {d.event_link && (
          <a href={d.event_link} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 text-xs bg-[#f9f8f5] border border-[#1A2B4A]/10 px-3 py-2 rounded-sm hover:border-[#C9A84C] transition-colors">
            <CalendarClock className="w-4 h-4 text-[#1A2B4A]" /> Apri evento in Calendar
          </a>
        )}
        {wa && (
          <a data-testid="chat-wa-link" href={wa} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 text-xs bg-[#276749] text-white px-3 py-2 rounded-sm">
            <MessageCircle className="w-4 h-4" /> Invia su WhatsApp (conferma manuale)
          </a>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full max-h-screen">
      <div className="px-5 py-3 border-b border-[#1A2B4A]/10 bg-white flex items-center gap-2">
        <MessageCircle className="w-4 h-4 text-[#C9A84C]" />
        <span className="text-xs uppercase tracking-[0.15em] text-[#4a5568]">{ASSISTANT_NAME}</span>
      </div>

      <VeraBriefing />

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((m) => (
          <div key={m.id} data-testid={`chat-msg-${m.role}`} className="animate-fade-up">
            {m.role === "user" ? (
              <div className="ml-8 bg-[#f9f8f5] border border-[#1A2B4A]/10 p-3 rounded-sm text-sm text-right">{m.text}</div>
            ) : (
              <div className="mr-4 bg-white border-l-4 border-[#C9A84C] shadow-sm p-3 text-sm whitespace-pre-line">
                {m.text}
                {m.data && renderExtra(m)}
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="mr-4 flex items-center gap-2 text-xs text-[#4a5568]">
            <Loader2 className="w-4 h-4 animate-spin text-[#C9A84C]" /> Elaboro…
          </div>
        )}
        {messages.length <= 1 && (
          <div className="pt-2 space-y-2">
            <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-[#8a93a6]">Prova a scrivere</p>
            {SUGGESTIONS.filter((s) => !s.soldi || vedeSoldi).map((s, i) => (
              <button key={s.t} data-testid={`chat-suggestion-${i}`} onClick={() => send(s.t)}
                className="block w-full text-left text-xs text-[#1A2B4A] bg-[#f9f8f5] border border-[#1A2B4A]/10 px-3 py-2 rounded-sm hover:border-[#C9A84C] transition-colors">
                {s.t}
              </button>
            ))}
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="p-3 border-t border-[#1A2B4A]/10 bg-white">
        <div className="flex items-end gap-2 border border-[#1A2B4A]/20 rounded-sm focus-within:border-[#C9A84C] px-3 py-2">
          <textarea data-testid="chat-input-field" rows={1} value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Scrivi un comando…"
            className="flex-1 resize-none bg-transparent text-sm focus:outline-none max-h-24" />
          <button data-testid="chat-submit-button" onClick={() => send()} disabled={loading}
            className="text-[#1A2B4A] hover:text-[#C9A84C] transition-colors">
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>

      {pending && (
        <div className="fixed inset-0 z-50 bg-[#16233c]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="human-confirmation-modal">
          <div className="bg-white rounded-lg max-w-sm w-full p-4 border-t-2 border-[#C9A84C] shadow-lg">
            <div className="flex items-center gap-2 mb-3 text-[#9b2c2c]">
              <AlertTriangle className="w-5 h-5" />
              <h3 className="font-serif text-xl text-[#1A2B4A]">Conferma richiesta</h3>
            </div>
            <p className="text-sm mb-6 whitespace-pre-line" data-testid="human-confirmation-modal-text">
              {pendingText || "Vuoi procedere con questa operazione?"}
            </p>
            <div className="flex justify-end gap-3">
              <button data-testid="human-confirmation-modal-cancel" onClick={() => confirm(false)}
                className="px-4 py-2 text-sm border border-[#1A2B4A] rounded-sm hover:bg-[#f9f8f5]">Annulla</button>
              <button data-testid="human-confirmation-modal-accept" onClick={() => confirm(true)}
                className="px-4 py-2 text-sm bg-[#9b2c2c] text-white rounded-sm hover:opacity-90">Conferma</button>
            </div>
          </div>
        </div>
      )}
      {docPreset && (
        <DocumentoDialog open={!!docPreset} onClose={() => setDocPreset(null)}
          clienteId={docPreset.cliente_id} praticaId={docPreset.pratica_id} preset={docPreset}
          onGenerated={(res) => { setDocPreset(null); push({ role: "assistant", text: `Documento «${res.documento.label}» generato.`, action: "documento", data: { documento: res.documento, wa_link: res.wa_link, drive_link: res.drive_link } }); refresh(); }} />
      )}
    </div>
  );
}
